import { useEffect, useState, type FormEvent } from 'react';
import { can, DETECTOR_STATUS_LABEL, DetectorCreate, DetectorUpdate, VehicleCreate, VehicleUpdate, type Detector, type Vehicle } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { useMe } from '../lib/me';
import { Dialog, useToast } from '../components/ui';

/** 車両と検知器の登録・直す・無効にする（無効にすると配送アプリの点呼で選べなくなる。過去の記録は残る） */
export function VehiclesPage() {
  const me = useMe();
  const toast = useToast();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [detectors, setDetectors] = useState<Detector[]>([]);
  const load = () => {
    api.get<{ vehicles: Vehicle[] }>('/v1/office/vehicles').then((r) => setVehicles(r.vehicles));
    api.get<{ detectors: Detector[] }>('/v1/office/detectors').then((r) => setDetectors(r.detectors));
  };
  useEffect(load, []);
  const canEdit = !!me && can(me.role, 'register');
  const [editV, setEditV] = useState<Vehicle | null>(null);
  const [editD, setEditD] = useState<Detector | null>(null);
  const state = (s: keyof typeof VEHICLE_STATUS | keyof typeof DETECTOR_STATUS_LABEL, labels: Record<string, string>) => s === 'active' ? labels[s] : <span className="muted">{labels[s]}</span>;
  return (
    <div className="page" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
      <section className="panel">
        <div style={{ padding: '14px 16px 0' }}><b>車両</b></div>
        <table><thead><tr><th>呼び名</th><th>車両ナンバー</th><th>状態</th>{canEdit && <th></th>}</tr></thead>
          <tbody>{vehicles.map((v) => <tr key={v.id}><td><b>{v.nickname}</b></td><td>{v.plateNumber}</td><td>{state(v.status, VEHICLE_STATUS)}</td>
            {canEdit && <td><button className="btn sm" onClick={() => setEditV(v)} aria-label={`${v.nickname}を直す`}>直す</button></td>}</tr>)}</tbody></table>
        {canEdit && <AddForm fields={[['plateNumber', '車両ナンバー（例：札幌 400 あ 12-34）'], ['nickname', '呼び名（例：2t 1号車）']]} schema={VehicleCreate} path="/v1/office/vehicles" onDone={() => { load(); toast('車両を登録しました'); }} />}
      </section>
      <section className="panel">
        <div style={{ padding: '14px 16px 0' }}><b>アルコール検知器</b></div>
        <table><thead><tr><th>名前</th><th>型番</th><th>状態</th>{canEdit && <th></th>}</tr></thead>
          <tbody>{detectors.map((d) => <tr key={d.id}><td><b>{d.name}</b></td><td>{d.model ?? '—'}</td><td>{state(d.status, DETECTOR_STATUS_LABEL)}</td>
            {canEdit && <td><button className="btn sm" onClick={() => setEditD(d)} aria-label={`${d.name}を直す`}>直す</button></td>}</tr>)}</tbody></table>
        {canEdit && <AddForm fields={[['name', '名前（例：検知器 A-02）'], ['model', '型番（任意）']]} schema={DetectorCreate} path="/v1/office/detectors" onDone={() => { load(); toast('検知器を登録しました'); }} />}
      </section>
      {editV && <EditDialog title="車両を直す" version={editV.version} path={`/v1/office/vehicles/${editV.id}`} schema={VehicleUpdate}
        fields={[['plateNumber', '車両ナンバー'], ['nickname', '呼び名']]} statuses={VEHICLE_STATUS} initial={{ plateNumber: editV.plateNumber, nickname: editV.nickname, status: editV.status }}
        onClose={() => setEditV(null)} onDone={() => { setEditV(null); load(); toast('車両を直しました'); }} />}
      {editD && <EditDialog title="検知器を直す" version={editD.version} path={`/v1/office/detectors/${editD.id}`} schema={DetectorUpdate}
        fields={[['name', '名前'], ['model', '型番（任意）']]} statuses={DETECTOR_STATUS_LABEL} initial={{ name: editD.name, model: editD.model ?? '', status: editD.status }}
        onClose={() => setEditD(null)} onDone={() => { setEditD(null); load(); toast('検知器を直しました'); }} />}
    </div>
  );
}

const VEHICLE_STATUS = { active: '有効', inactive: '無効' } as const;

function EditDialog({ title, version, path, schema, fields, statuses, initial, onClose, onDone }: {
  title: string; version: number; path: string; schema: typeof VehicleUpdate | typeof DetectorUpdate; fields: [string, string][]; statuses: Record<string, string>;
  initial: Record<string, string>; onClose: () => void; onDone: () => void;
}) {
  const [v, setV] = useState(initial);
  const [err, setErr] = useState('');
  const save = async () => {
    const body = Object.fromEntries(Object.entries(v).filter(([, x]) => x.trim()));
    const r = schema.safeParse(body);
    if (!r.success) { setErr(r.error.issues[0]?.message ?? '入力を確かめてください'); return; }
    try { await api.patch(path, r.data, { ifMatch: version }); onDone(); } catch (e) { setErr(e instanceof HttpError ? e.message : '保存できませんでした'); }
  };
  return (
    <Dialog title={title} onClose={onClose}>
      {fields.map(([k, label]) => <div className="field" key={k}><label htmlFor={`ed-${k}`}>{label}</label><input id={`ed-${k}`} value={v[k] ?? ''} onChange={(e) => setV({ ...v, [k]: e.target.value })} /></div>)}
      <div className="field"><label htmlFor="ed-status">状態</label><select id="ed-status" value={v.status} onChange={(e) => setV({ ...v, status: e.target.value })}>
        {Object.entries(statuses).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
      <p className="muted" style={{ margin: 0 }}>「{statuses.active}」以外にすると、点呼で選べなくなります。過去の記録は残ります。</p>
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={onClose}>やめる</button><button className="btn pri" onClick={() => void save()}>保存する</button></div>
    </Dialog>
  );
}

function AddForm({ fields, schema, path, onDone }: { fields: [string, string][]; schema: typeof VehicleCreate | typeof DetectorCreate; path: string; onDone: () => void }) {
  const [v, setV] = useState<Record<string, string>>({});
  const [err, setErr] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = Object.fromEntries(Object.entries(v).filter(([, x]) => x.trim()));
    const r = schema.safeParse(body);
    if (!r.success) { setErr(r.error.issues[0]?.message ?? '入力を確かめてください'); return; }
    try { await api.post(path, r.data); setV({}); setErr(''); onDone(); } catch (er) { setErr(er instanceof HttpError ? er.message : '登録できませんでした'); }
  };
  return (
    <form onSubmit={submit} style={{ padding: 16, display: 'grid', gap: 8, borderTop: '1px solid var(--line)' }}>
      {fields.map(([k, label]) => <div className="field" key={k}><label htmlFor={`${path}-${k}`}>{label}</label><input id={`${path}-${k}`} value={v[k] ?? ''} onChange={(e) => setV({ ...v, [k]: e.target.value })} /></div>)}
      {err && <p className="err" role="alert">{err}</p>}
      <button className="btn pri" style={{ justifySelf: 'end' }}>登録する</button>
    </form>
  );
}
